import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Observable, switchMap, take, throwError, catchError, of, Subscription } from "rxjs";
import { AUTH_API, RefreshQueueService } from "../../auth/services/auth/auth.service";
import { JwtService } from "../services/jwt/jwt.service";
import {CentralConfigsService} from "../../../public/authentication/services/centralConfigs.service";
import {environment} from "../../../../environments/environment";
import {SimpleLocaleService} from "@core/services/simple-locale.service";

@Injectable({
    providedIn: 'root'
})
export class JWTInterceptor implements HttpInterceptor, OnDestroy {
    private domain: string = '';
    private _language = 'en';
    private isRefreshing = false;
    private domainSubscription: Subscription;

    constructor(
        private refreshQueueService: RefreshQueueService,
        private jwtService: JwtService,
        private centralConfigsService: CentralConfigsService,
        private simpleLocaleService: SimpleLocaleService,
    ) {
        // Initialize domain
        this.domain = this.centralConfigsService.getEnvironment();

        // Subscribe to domain changes
        this.domainSubscription = this.centralConfigsService._domain.subscribe(newDomain => {
            if (newDomain) {
                this.domain = newDomain;
                console.log('Domain updated in interceptor:', this.domain);
            }
        });
    }

    ngOnDestroy() {
        if (this.domainSubscription) {
            this.domainSubscription.unsubscribe();
        }
    }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

        request = this.addBaseUrl(request);
        return this.handleRequest(request, next);
    }

    private addBaseUrl(request: HttpRequest<unknown>): HttpRequest<unknown> {
        this._language = this.simpleLocaleService.getCurrentLocale();
        // Check if the URL is already a full URL (starts with http:// or https://)
        const isFullUrl = request.url.startsWith('http://') || request.url.startsWith('https://');

        // Get current domain
        const currentDomain = this.domain || this.centralConfigsService.getEnvironment() || environment.apiUrl;

        if (!currentDomain) {
            console.warn('No domain available for request:', request.url);
            return request;
        }

        return request.clone({
            url: isFullUrl ? request.url : `${currentDomain}${request.url}`,
            setHeaders: {
                'X-Requested-With': 'XMLHttpRequest',
                'accept-language': this._language
            }
        });
    }

    private isApiUrl(url: string): boolean {
        const isTokenLoginUrl: boolean = url.endsWith(AUTH_API.Login);
        const isTokenRefreshUrl: boolean = url.includes(AUTH_API.Refresh(''));
        const isSignalRUrl: boolean = url.startsWith("/hub/");
        return !isTokenLoginUrl && !isTokenRefreshUrl && !isSignalRUrl;
    }

    private isAuthEndpoint(url: string): boolean {
        const authEndpoints = [
            AUTH_API.Login,
            AUTH_API.LoginSSO,
            AUTH_API.CheckSSO,
            'api/auth/refresh',
            '/hub/'
        ];

        return authEndpoints.some(endpoint => url.includes(endpoint));
    }

    private addAuthHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    public handleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const tokenPair = this.jwtService.getToken();

        // Allow refresh token API calls to proceed without token validation
        if (request.url.includes(AUTH_API.Refresh(''))) {
            return next.handle(request);
        }

        if (!tokenPair?.accessToken) {
            return next.handle(request);
        }

        // If access token is still valid, use it
        if (!this.jwtService.isTokenExpired(tokenPair.accessToken)) {
            return next.handle(this.addAuthHeader(request, tokenPair.accessToken));
        }

        // If refresh token is expired, logout
        if (this.jwtService.isTokenExpired(tokenPair.refreshToken)) {
            this.refreshQueueService.logoutQueue.next();
            return next.handle(request);
        }

        // If already refreshing, wait for the refresh to complete
        if (this.isRefreshing) {
            return this.refreshQueueService.refreshToken$.pipe(
                take(1),
                switchMap(result => {
                    if (!result?.value?.tokenPair?.accessToken) {
                        return next.handle(request);
                    }
                    return next.handle(this.addAuthHeader(request, result.value.tokenPair.accessToken));
                })
            );
        }

        // Start refresh process
        this.isRefreshing = true;
        console.log('Refreshing token...');
        this.refreshQueueService.pushRefresh();

        return this.refreshQueueService.refreshToken$.pipe(
            take(1),
            switchMap(result => {
                this.isRefreshing = false;
                console.log('Refresh result:', result ? 'success' : 'failed');
                if (!result?.value?.tokenPair?.accessToken) {
                    return next.handle(request);
                }
                return next.handle(this.addAuthHeader(request, result.value.tokenPair.accessToken));
            }),
            catchError(error => {
                this.isRefreshing = false;
                if (error.status === 401) {
                    this.refreshQueueService.logoutQueue.next();
                }
                return throwError(() => error);
            })
        );
    }
}
